<template>
  <div class="translate-container">
    <div class="translate-header">
      <div class="actions">
        <el-button :type="isRecording ? 'danger' : 'primary'" @click="toggleRecording" size="small" :loading="actionLoading"
          :disabled="actionLoading || (!isRecording && !isConnected)">
          {{ isRecording ? "停止识别" : startBtnText }}
        </el-button>
        <el-button type="warning" size="small" v-if="showConfigDialog" @click="showConfig = true">
          配置
        </el-button>
      </div>
      <!-- 音频波形显示 -->
      <AudioWaveform v-if="showAudioWaveform" ref="audioWaveform" :isVisible="isRecording" :canvasWidth="300"
        :canvasHeight="80" />
    </div>

    <!-- 配置信息显示 -->
    <div class="config-display" v-if="showDetailConfig">
      <div class="config-item">
        <span class="config-label">源语言:</span>
        <span class="config-value">{{
          getLanguageName(config.sourceLanguage)
          }}</span>
      </div>
      <div class="config-item">
        <span class="config-label">目标语言:</span>
        <span class="config-value">{{
          getLanguageName(config.targetLanguage)
          }}</span>
      </div>
      <div class="config-item">
        <span class="config-label">采样率:</span>
        <span class="config-value">{{ config.sampleRate }} Hz</span>
      </div>
      <div class="config-item">
        <span class="config-label">断句的静音阈值:</span>
        <span class="config-value">{{ config.maxEndSilence }}ms</span>
      </div>
      <div class="config-item">
        <span class="config-label">声音源:</span>
        <span class="config-value">{{
          getAudioSourceName(config.audioSource)
          }}</span>
      </div>
    </div>
    <!-- 翻译配置弹窗 -->
    <el-dialog title="翻译配置" :visible.sync="showConfig" :append-to-body="true" width="600px" @close="showConfig = false">
      <div>
        <div class="config-grid">
          <div class="config-row">
            <label>源语言:</label>
            <el-select v-model="config.sourceLanguage" :disabled="isRecording" style="width: 100%">
              <el-option :disabled="isSourceLanguageDisabled(language.value)" v-for="language in sourceLanguages"
                :key="language.value" :value="language.value" :label="language.label"></el-option>
            </el-select>
          </div>
          <div class="config-row">
            <label>目标语言:</label>
            <el-select v-model="config.targetLanguage" :disabled="isRecording" style="width: 100%">
              <el-option :disabled="isTargetLanguageDisabled(language.value)" v-for="language in targetLanguages"
                :key="language.value" :value="language.value" :label="language.label"></el-option>
            </el-select>
          </div>
          <div class="config-row">
            <label>采样率:</label>
            <el-select v-model="config.sampleRate" :disabled="isRecording" style="width: 100%">
              <el-option value="16000" label="16000 Hz"></el-option>
              <el-option value="8000" label="8000 Hz"></el-option>
            </el-select>
          </div>
          <div class="config-row">
            <label>断句的静音时长阈值（毫秒）:</label>
            <el-input-number v-model="config.maxEndSilence" :disabled="isRecording" style="width: 100%" :min="200"
              :max="6000" :step="100"></el-input-number>
          </div>
          <div class="config-row">
            <label>声音源:</label>
            <el-select v-model="config.audioSource" :disabled="isRecording" style="width: 100%"
              @change="handleAudioSourceChange">
              <el-option-group label="麦克风设备">
                <el-option v-for="device in audioDevices" :key="device.deviceId" :value="device.deviceId" :label="device.label || `麦克风 ${device.deviceId.substring(0, 8)}`
                  "></el-option>
              </el-option-group>
              <el-option-group label="系统声音">
                <el-option value="system" label="系统声音"></el-option>
              </el-option-group>
            </el-select>
            <div v-if="config.audioSource === 'system'" class="audio-source-tip">
              <small>💡 系统音频捕获说明：</small>
              <small>•
                需要选择屏幕共享，建议选择"浏览器标签页"以获得最佳音频支持</small>
              <small>• macOS 用户：只有共享浏览器标签页时才支持音频捕获</small>
              <small>• Windows 用户：选择全屏或标签页时都支持音频捕获</small>
            </div>
          </div>
          <div class="config-row" v-if="!audioDevices.length">
            <el-button type="text" @click="refreshAudioDevices" :loading="loadingDevices">
              {{ loadingDevices ? "正在获取设备..." : "刷新音频设备" }}
            </el-button>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showConfig = false">取消</el-button>
        <el-button type="primary" @click="saveConfig" :disabled="isRecording">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import AudioWaveform from "./components/AudioWaveform/index.vue";
export default {
  props: {
    // 是否显示音频波形
    showAudioWaveform: {
      type: Boolean,
      default: false,
    },
    // 是否显示详细配置
    showDetailConfig: {
      type: Boolean,
      default: false,
    },
    // 是否显示配置按钮
    showConfigDialog: {
      type: Boolean,
      default: false
    },
    // 是否需要翻译
    needTranslate: {
      type: Boolean,
      default: true
    },
    // 开始按钮文本
    startBtnText: {
      type: String,
      default: "开始识别"
    }
  },
  components: {
    AudioWaveform,
  },
  data() {
    return {
      ws: null,
      mediaRecorder: null,
      audioContext: null,
      isRecording: false,
      isConnected: false,
      isConnecting: false,
      isReconnecting: false,
      actionLoading: false,
      startDeferred: null,
      stopDeferred: null,
      showConfig: false,
      config: {
        sourceLanguage: "",
        targetLanguage: "zh",
        sampleRate: 16000,
        audioSource: "", // 将在获取设备后设置为第一个麦克风设备
        maxEndSilence: 300,
      },
      error: null,
      audioDevices: [], // 存储音频设备列表
      loadingDevices: false, // 设备加载状态
      recordingStartTime: null, // 录音开始时间
      sourceLanguages: [
        { value: "zh", label: "中文" },
        { value: "en", label: "英文" },
        { value: "ja", label: "日语" },
        { value: "yue", label: "粤语" },
        { value: "ko", label: "韩语" },
        { value: "de", label: "德语" },
        { value: "fr", label: "法语" },
        { value: "ru", label: "俄语" },
        { value: "it", label: "意大利语" },
        { value: "es", label: "西班牙语" },
      ],
      targetLanguages: [
        { value: "zh", label: "中文" },
        { value: "en", label: "英文" },
        { value: "ja", label: "日语" },
        { value: "ko", label: "韩语" },
      ],
      isToDestroy: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
    };
  },
  computed: {
    canStart() {
      return this.isConnected && !this.isRecording;
    },
    statusClass() {
      if (this.error) return "error";
      if (this.isRecording) return "recording";
      if (this.isConnected) return "connected";
      if (this.isConnecting) return "connecting";
      return "";
    },
    statusText() {
      if (this.error) return `错误: ${this.error}`;
      if (this.isRecording) return "正在录音...";
      if (this.isConnected) return "Websocket已连接";
      if (this.isReconnecting) return "正在重连...";
      if (this.isConnecting) return "正在连接...";
      return "未连接";
    },
    // 定义支持的翻译语言组合
    supportedTranslations() {
      return {
        // 中->英，中->日，中->韩
        zh: ["en", "ja", "ko"],
        // 英->中，英->日，英->韩
        en: ["zh", "ja", "ko"],
        // 日、韩、粤、德、法、俄、意、西 -> 中、英
        ja: ["zh", "en"],
        ko: ["zh", "en"],
        yue: ["zh", "en"],
        de: ["zh", "en"],
        fr: ["zh", "en"],
        ru: ["zh", "en"],
        it: ["zh", "en"],
        es: ["zh", "en"],
      };
    },
  },
  mounted() {
    this.connectWebSocket();
    if (this.showConfigDialog) {
      this.getAudioDevices();
    }
  },
  watch: {
    // 监听源语言变化
    "config.sourceLanguage"(newSourceLang) {
      // 如果当前选择的目标语言不支持从新的源语言翻译，则重置目标语言
      if (newSourceLang && this.config.targetLanguage) {
        const supportedTargets = this.supportedTranslations[newSourceLang];
        if (
          !supportedTargets ||
          !supportedTargets.includes(this.config.targetLanguage)
        ) {
          // 重置为第一个支持的目标语言，如果没有则重置为中文
          this.config.targetLanguage =
            supportedTargets && supportedTargets.length > 0
              ? supportedTargets[0]
              : "zh";
        }
      }
    },
    // 监听目标语言变化
    "config.targetLanguage"(newTargetLang) {
      // 如果当前选择的源语言不支持翻译到新的目标语言，则重置源语言
      if (newTargetLang && this.config.sourceLanguage) {
        const supportedTargets =
          this.supportedTranslations[this.config.sourceLanguage];
        if (!supportedTargets || !supportedTargets.includes(newTargetLang)) {
          // 找到支持该目标语言的第一个源语言，如果没有则重置为英文
          const compatibleSource = Object.keys(this.supportedTranslations).find(
            (source) =>
              this.supportedTranslations[source] &&
              this.supportedTranslations[source].includes(newTargetLang)
          );
          this.config.sourceLanguage = compatibleSource || "en";
        }
      }
    },
  },
  methods: {
    // 判断源语言是否应该被禁用
    isSourceLanguageDisabled(languageValue) {
      // 如果当前语言是已选择的目标语言，则禁用
      if (this.config.targetLanguage === languageValue) {
        return true;
      }

      // 如果已选择目标语言，检查源语言是否支持翻译到该目标语言
      if (this.config.targetLanguage) {
        return (
          !this.supportedTranslations[languageValue] ||
          !this.supportedTranslations[languageValue].includes(
            this.config.targetLanguage
          )
        );
      }

      // 如果没有选择目标语言，检查该语言是否有任何支持的目标语言
      return !this.supportedTranslations[languageValue];
    },

    // 判断目标语言是否应该被禁用
    isTargetLanguageDisabled(languageValue) {
      // 如果当前语言是已选择的源语言，则禁用
      if (this.config.sourceLanguage === languageValue) {
        return true;
      }

      // 如果已选择源语言，检查目标语言是否支持从该源语言翻译
      if (this.config.sourceLanguage) {
        return (
          !this.supportedTranslations[this.config.sourceLanguage] ||
          !this.supportedTranslations[this.config.sourceLanguage].includes(
            languageValue
          )
        );
      }

      // 如果没有选择源语言，所有目标语言都可用（因为目标语言只有四种核心语言）
      return false;
    },
    // 创建一个deferred对象，便于等待WS事件
    createDeferred(timeoutMs = 10000) {
      let resolveFn, rejectFn;
      const promise = new Promise((resolve, reject) => {
        resolveFn = resolve;
        rejectFn = reject;
      });
      const timer = setTimeout(() => {
        rejectFn && rejectFn(new Error("操作超时"));
      }, timeoutMs);
      return {
        promise,
        resolve: (value) => {
          clearTimeout(timer);
          resolveFn && resolveFn(value);
        },
        reject: (error) => {
          clearTimeout(timer);
          rejectFn && rejectFn(error);
        },
      };
    },
    formateBalance(balance) {
      // 保留一位小数
      return balance ? balance.toFixed(0) : 0;
    },
    async toggleRecording() {
      if (this.actionLoading) return;

      if (this.isRecording) {
        try {
          this.actionLoading = true;

          // 等待任务真正结束
          this.stopDeferred = this.createDeferred(10000);
          this.stopRecording();

          // 安全地等待停止完成
          if (this.stopDeferred && this.stopDeferred.promise) {
            await this.stopDeferred.promise.catch(() => { });
          }

          // 在actionLoading完成后才改变isRecording状态
          this.isRecording = false;
          this.$message.warning("翻译已停止");
        } catch (error) {
          console.error("停止录音时出错:", error);
          this.isRecording = false;

        } finally {
          this.actionLoading = false;
          // 清理deferred对象
          this.stopDeferred = null;
        }
      } else {
        // 开始翻译前检查余额
        this.actionLoading = true;

        try {

          // 等待任务真正开始
          this.startDeferred = this.createDeferred(10000);
          this.startRecording();

          // 安全地等待开始完成
          if (this.startDeferred && this.startDeferred.promise) {
            await this.startDeferred.promise.catch(() => { });
          }

          // 在actionLoading完成后才改变isRecording状态
          this.isRecording = true;
        } catch (error) {
          console.error("开始录音时出错:", error);
          this.isRecording = false;
        } finally {
          this.actionLoading = false;
          // 清理deferred对象
          this.startDeferred = null;
        }
      }
    },
    saveConfig() {
      // 验证语言组合是否支持
      if (this.config.sourceLanguage && this.config.targetLanguage) {
        const supportedTargets =
          this.supportedTranslations[this.config.sourceLanguage];
        if (
          !supportedTargets ||
          !supportedTargets.includes(this.config.targetLanguage)
        ) {
          this.$message.error("不支持的语言组合，请重新选择");
          return;
        }
      }

      this.showConfig = false;
      // 如果正在录音，需要重新启动以应用新配置
      if (this.isRecording) {
        // 使用toggleRecording来确保正确的状态管理
        this.toggleRecording()
          .then(() => {
            // 等待停止完成后，再次调用toggleRecording来开始
            this.$nextTick(() => {
              this.toggleRecording();
            });
          })
          .catch((error) => {
            console.error("保存配置时出错:", error);
          });
      }
    },

    connectWebSocket(isManualReconnect = false) {
      if (isManualReconnect) {
        this.isReconnecting = true;
      } else {
        this.isConnecting = true;
      }
      this.error = null;

      try {
        this.ws = new WebSocket("wss://team-chat.learningfirst.cn:8080");

        this.ws.onopen = () => {
          console.log("WebSocket连接成功");
          this.isConnected = true;
          this.isConnecting = false;
          this.isReconnecting = false;
          this.reconnectAttempts = 0; // 重置重连次数
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleServerMessage(data);
          } catch (error) {
            console.error("解析服务器消息错误:", error);
          }
        };

        this.ws.onclose = () => {
          console.log("WebSocket连接关闭");
          this.isConnected = false;
          this.isConnecting = false;
          this.isReconnecting = false;

          // 清理deferred对象
          this.clearDeferredObjects();

          // WebSocket连接断开时，如果没有在loading中，才重置isRecording
          if (!this.actionLoading) {
            this.isRecording = false;
          }

          // 尝试重连
          if (
            !this.isToDestroy &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            this.reconnectAttempts++;
            // 立即设置重连状态并通知父组件
            this.isReconnecting = true;

            const delay = Math.min(
              1000 * Math.pow(2, this.reconnectAttempts - 1),
              10000
            ); // 指数退避，最大10秒
            console.log(
              `翻译服务尝试第${this.reconnectAttempts}次重连，${delay}ms后开始...`
            );
            setTimeout(() => {
              if (!this.isConnected && !this.isToDestroy) {
                this.connectWebSocket(true);
              }
            }, delay);
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.isReconnecting = false;
            console.log("翻译服务已达到最大重连次数，停止重连");
            this.$message.error("翻译服务连接失败，请手动重连或刷新页面");
          }
        };

        this.ws.onerror = (error) => {
          console.error("WebSocket错误:", error);
          this.error = "连接服务器失败";
          this.isConnecting = false;
          this.isReconnecting = false;
        };
      } catch (error) {
        this.error = "无法建立WebSocket连接";
        this.isConnecting = false;
        this.isReconnecting = false;
      }
    },

    handleServerMessage(data) {
      switch (data.type) {
        case "task-started":
          console.log("任务启动成功");
          if (this.startDeferred) {
            this.startDeferred.resolve();
            this.startDeferred = null;
          }
          break;

        case "result":
          this.processRecognitionResult(data.data);
          break;

        case "task-finished":
          console.log("任务完成");
          if (this.stopDeferred) {
            this.stopDeferred.resolve();
            this.stopDeferred = null;
          }
          break;

        case "error":
          this.error = data.message;
          if (this.startDeferred) {
            this.startDeferred.reject(new Error(this.error || "任务启动失败"));
            this.startDeferred = null;
          }
          if (this.stopDeferred) {
            this.stopDeferred.resolve();
            this.stopDeferred = null;
          }
          break;
      }
    },

    processRecognitionResult(data) {
      if (this.needTranslate) {
        // 处理翻译结果
        if (data.translations) {
          data.translations.forEach((translation) => {
            if (translation.sentence_end) {
              // 句子结束时添加到文本框
              this.addToTextarea(translation.text);
            }
          });
        }
      } else {
        // 处理语音识别结果
        if (data.transcription) {
          const transcription = data.transcription;
          if (transcription.sentence_end) {
            // 句子结束时添加到文本框
            this.addToTextarea(transcription.text, "语音识别");
          }
        }
      }
    },

    addToTextarea(text) {
      this.$emit("addText", text);
    },

    async startRecording() {
      if (this.isRecording) return;

      // 检查WebSocket连接状态
      if (!this.isConnected) {
        this.$message.error("翻译服务未连接，请等待连接完成");
        return;
      }

      // 验证语言组合是否支持
      if (this.config.sourceLanguage && this.config.targetLanguage) {
        const supportedTargets =
          this.supportedTranslations[this.config.sourceLanguage];
        if (
          !supportedTargets ||
          !supportedTargets.includes(this.config.targetLanguage)
        ) {
          this.$message.error("不支持的语言组合，请重新选择");
          return;
        }
      }

      try {
        // 检查浏览器音频支持
        this.checkAudioSupport();

        let stream;

        if (this.config.audioSource === "system") {
          // 请求系统音频权限
          // 注意：getDisplayMedia API 要求必须包含视频轨道，因此 video 必须为 true
          stream = await navigator.mediaDevices.getDisplayMedia({
            audio: {
              sampleRate: this.config.sampleRate,
              channelCount: 1,
            },
            video: true, // 必须为 true，这是 Web 标准规范的要求
          });

          // 获取音频和视频轨道
          const audioTracks = stream.getAudioTracks();
          const videoTracks = stream.getVideoTracks();

          // 检查是否成功获取到音频轨道
          if (audioTracks.length === 0) {
            // 停止所有轨道
            stream.getTracks().forEach((track) => track.stop());
            throw new Error(
              "未能获取到系统音频，请确保选择了支持音频捕获的共享选项（如浏览器标签页）"
            );
          }

          // 停止视频轨道以节省资源（我们只需要音频）
          videoTracks.forEach((track) => {
            track.stop();
            console.log("已停止视频轨道以节省资源");
          });

          // 创建只包含音频的新流
          stream = new MediaStream(audioTracks);
        } else {
          // 使用特定的麦克风设备
          const audioConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          };

          // 如果选择了特定的设备ID，添加设备约束
          if (
            this.config.audioSource &&
            this.config.audioSource !== "microphone"
          ) {
            audioConstraints.deviceId = { exact: this.config.audioSource };
          }

          // 尝试获取音频流，如果失败则使用基本约束
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: audioConstraints,
            });
          } catch (error) {
            console.warn(
              "Advanced audio constraints failed, trying basic constraints:",
              error
            );
            // 降级到基本约束
            const basicConstraints = {};
            if (
              this.config.audioSource &&
              this.config.audioSource !== "microphone"
            ) {
              basicConstraints.deviceId = { exact: this.config.audioSource };
            }
            stream = await navigator.mediaDevices.getUserMedia({
              audio: basicConstraints,
            });
          }
        }

        // 启动任务
        this.$message.success("开始翻译");
        let config = {
          sampleRate: this.config.sampleRate,
          targetLanguages: [this.config.targetLanguage],
          sourceLanguage: this.config.sourceLanguage,
          maxEndSilence: this.config.maxEndSilence,
        };
        this.ws.send(
          JSON.stringify({
            type: "start-task",
            config: config,
          })
        );

        // 设置音频上下文
        this.audioContext = new (window.AudioContext ||
          window.webkitAudioContext)({
            sampleRate: this.config.sampleRate,
          });

        const source = this.audioContext.createMediaStreamSource(stream);

        // 使用现代的 AudioWorklet 或 fallback 到 ScriptProcessor
        let processor;
        if (this.audioContext.audioWorklet) {
          try {
            // 尝试使用 AudioWorklet (现代方法)
            await this.audioContext.audioWorklet
              .addModule("/js/audio-processor.js")
              .catch(() => {
                throw new Error("AudioWorklet not available");
              });
            processor = new AudioWorkletNode(
              this.audioContext,
              "audio-processor"
            );
          } catch (error) {
            console.warn(
              "AudioWorklet not available, falling back to ScriptProcessor"
            );
            processor = this.createScriptProcessorFallback();
          }
        } else {
          // 降级到 ScriptProcessor (已废弃但兼容性更好)
          processor = this.createScriptProcessorFallback();
        }

        // 设置音频处理
        if (processor.port) {
          // AudioWorklet
          processor.port.onmessage = (event) => {
            if (this.isRecording && event.data.audioData) {
              this.processAudioData(event.data.audioData);
            }
          };
        } else {
          // ScriptProcessor
          processor.onaudioprocess = (event) => {
            if (this.isRecording) {
              const inputData = event.inputBuffer.getChannelData(0);
              this.processAudioData(inputData);
            }
          };
        }

        source.connect(processor);
        if (processor.connect) {
          processor.connect(this.audioContext.destination);
        }

        this.mediaRecorder = { stream, processor, source };
        this.error = null;

        // 记录开始时间并启动定时消费余额
        this.recordingStartTime = Date.now();
        // 若服务端未发task-started，也给一个兜底的resolve（避免loading长时间不消）
        if (this.startDeferred && this.startDeferred.resolve) {
          setTimeout(() => {
            if (this.startDeferred && this.startDeferred.resolve) {
              this.startDeferred.resolve();
              this.startDeferred = null;
            }
          }, 300);
        }
      } catch (error) {
        console.error("启动录音失败:", error);

        let errorMessage = "启动录音失败";

        if (error.name === "NotAllowedError") {
          if (this.config.audioSource === "system") {
            errorMessage = "屏幕共享权限被拒绝，请允许屏幕共享以获取系统音频";
          } else {
            errorMessage = "麦克风权限被拒绝，请在浏览器设置中允许访问麦克风";
          }
        } else if (error.name === "NotFoundError") {
          errorMessage = "未找到指定的音频设备，请检查设备连接";
        } else if (error.name === "NotSupportedError") {
          if (this.config.audioSource === "system") {
            errorMessage =
              "当前浏览器不支持系统音频捕获，请使用 Chrome、Edge 或 Firefox 最新版本";
          } else {
            errorMessage = "当前浏览器不支持音频录制功能，请使用现代浏览器";
          }
        } else if (error.name === "NotReadableError") {
          errorMessage = "音频设备被其他应用程序占用，请关闭其他录音应用";
        } else if (error.name === "OverconstrainedError") {
          errorMessage = "音频设备不满足要求，请选择其他音频源";
        } else if (
          error.name === "TypeError" &&
          this.config.audioSource === "system"
        ) {
          errorMessage =
            "系统音频捕获失败，请确保选择了支持音频的共享选项（建议选择浏览器标签页）";
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$message.error(errorMessage);
        this.error = errorMessage;

        // 重置状态
        this.isRecording = false;
        this.actionLoading = false;
      }
    },

    stopRecording() {
      // 重置音频波形显示
      if (this.$refs.audioWaveform) {
        this.$refs.audioWaveform.reset();
      }

      if (!this.mediaRecorder) {
        // 安全地处理deferred对象
        if (this.stopDeferred && this.stopDeferred.resolve) {
          this.stopDeferred.resolve();
          this.stopDeferred = null;
        }
        return;
      }

      try {
        // 停止媒体流
        this.mediaRecorder.stream.getTracks().forEach((track) => track.stop());

        // 断开音频节点
        if (
          this.mediaRecorder.processor &&
          this.mediaRecorder.processor.disconnect
        ) {
          this.mediaRecorder.processor.disconnect();
        }
        if (this.mediaRecorder.source && this.mediaRecorder.source.disconnect) {
          this.mediaRecorder.source.disconnect();
        }

        // 关闭音频上下文
        if (this.audioContext && this.audioContext.close) {
          this.audioContext.close();
        }

        this.mediaRecorder = null;
        this.audioContext = null;
      } catch (error) {
        console.error("停止录音时出错:", error);
      }

      // 通知服务器停止任务
      if (this.ws && this.isRecording) {
        try {
          this.ws.send(
            JSON.stringify({
              type: "stop-task",
            })
          );
        } catch (error) {
          console.error("发送停止任务消息失败:", error);
        }
      }

      // 兜底resolve，避免服务端未及时发task-finished
      if (this.stopDeferred && this.stopDeferred.resolve) {
        setTimeout(() => {
          if (this.stopDeferred && this.stopDeferred.resolve) {
            this.stopDeferred.resolve();
            this.stopDeferred = null;
          }
        }, 300);
      }
    },

    convertFloat32ToInt16(float32Array) {
      const int16Array = new Int16Array(float32Array.length);
      for (let i = 0; i < float32Array.length; i++) {
        const val = Math.max(-1, Math.min(1, float32Array[i]));
        int16Array[i] = val < 0 ? val * 0x8000 : val * 0x7fff;
      }
      return new Uint8Array(int16Array.buffer);
    },

    // 检查浏览器音频支持
    checkAudioSupport() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("当前浏览器不支持音频录制API");
      }

      if (!window.AudioContext && !window.webkitAudioContext) {
        throw new Error("当前浏览器不支持音频上下文API");
      }

      return true;
    },

    // 获取音频设备列表
    async getAudioDevices() {
      try {
        this.loadingDevices = true;

        // 检查浏览器支持
        this.checkAudioSupport();

        // 请求用户媒体权限以获取设备标签
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // 获取所有媒体设备
        const devices = await navigator.mediaDevices.enumerateDevices();

        // 过滤出音频输入设备
        this.audioDevices = devices.filter(
          (device) => device.kind === "audioinput"
        );

        // 停止临时流
        stream.getTracks().forEach((track) => track.stop());

        // 如果有设备且当前没有选择音频源，设置第一个麦克风设备为默认选择
        if (this.audioDevices.length > 0 && !this.config.audioSource) {
          this.config.audioSource = this.audioDevices[0].deviceId;
          console.log(
            "默认选择第一个麦克风设备:",
            this.audioDevices[0].label || this.audioDevices[0].deviceId
          );
        }
      } catch (error) {
        console.error("获取音频设备失败:", error);
        this.$message.warning("无法获取音频设备，请检查麦克风权限");
      } finally {
        this.loadingDevices = false;
      }
    },

    // 刷新音频设备
    refreshAudioDevices() {
      this.getAudioDevices();
    },

    // 处理音频源变化
    handleAudioSourceChange(value) {
      console.log("音频源已切换至:", value);
      // 如果正在录音，重新启动以应用新的音频源
      if (this.isRecording) {
        // 使用toggleRecording来确保正确的状态管理
        this.toggleRecording()
          .then(() => {
            // 等待停止完成后，再次调用toggleRecording来开始
            this.$nextTick(() => {
              this.toggleRecording();
            });
          })
          .catch((error) => {
            console.error("切换音频源时出错:", error);
          });
      }
    },

    getLanguageName(lang) {
      // 先从源语言数组中查找
      let language = this.sourceLanguages.find((l) => l.value === lang);
      // 如果没找到，再从目标语言数组中查找
      if (!language) {
        language = this.targetLanguages.find((l) => l.value === lang);
      }
      return language ? language.label : lang;
    },

    getAudioSourceName(audioSource) {
      if (audioSource === "system") {
        return "系统声音";
      }
      const device = this.audioDevices.find((d) => d.deviceId === audioSource);
      return device
        ? device.label || `麦克风 ${audioSource.substring(0, 8)}`
        : "未知设备";
    },

    // 创建 ScriptProcessor 降级方案
    createScriptProcessorFallback() {
      try {
        const processor = this.audioContext.createScriptProcessor(4096, 1, 1);
        return processor;
      } catch (error) {
        console.error("ScriptProcessor creation failed:", error);
        throw new Error("无法创建音频处理器");
      }
    },

    // 处理音频数据
    processAudioData(inputData) {
      // 更新音频波形显示
      if (this.$refs.audioWaveform) {
        this.$refs.audioWaveform.updateAudioData(inputData);
      }

      const audioData = this.convertFloat32ToInt16(inputData);
      const base64Data = btoa(String.fromCharCode.apply(null, audioData));

      this.ws.send(
        JSON.stringify({
          type: "audio-data",
          audioData: base64Data,
        })
      );
    },

    // 安全地清理deferred对象
    clearDeferredObjects() {
      if (this.startDeferred && this.startDeferred.reject) {
        this.startDeferred.reject(new Error("操作被取消"));
        this.startDeferred = null;
      }
      if (this.stopDeferred && this.stopDeferred.reject) {
        this.stopDeferred.reject(new Error("操作被取消"));
        this.stopDeferred = null;
      }
    },

    // 手动重连
    manualReconnect() {
      this.reconnectAttempts = 0; // 重置重连次数
      // 清理deferred对象
      this.clearDeferredObjects();
      if (this.ws) {
        this.ws.close();
      }
      this.connectWebSocket(true);
    },
  },

  beforeDestroy() {
    this.isToDestroy = true;

    // 清理deferred对象
    this.clearDeferredObjects();

    if (this.isRecording) {
      this.stopRecording();
    }
    if (this.ws) {
      this.ws.close();
    }
  },
};
</script>

<style lang="scss" scoped>
.translate-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 48px;
}

.translate-container .translate-header {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.translate-container .actions {
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.translate-container .translate-balance {
  grid-column: 3;
  justify-self: end;
}

.translate-balance {
  font-size: 14px;
  color: #666;
}

.container {
  background: white;
  border-radius: 20px;
  padding: 30px;
  box-sizing: border-box;
  width: 100%;
}

.controls {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;
}

.status {
  text-align: center;
  margin-bottom: 20px;
  padding: 10px;
  border-radius: 8px;
  font-weight: bold;
}

.status.connecting {
  background: #fff3cd;
  color: #856404;
}

.status.connected {
  background: #d4edda;
  color: #155724;
}

.status.recording {
  background: #f8d7da;
  color: #721c24;
}

.status.error {
  background: #f8d7da;
  color: #721c24;
}

.results {
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 20px;
  background: #fafafa;
}

.recording-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  background: #ff4757;
  border-radius: 50%;
  margin-right: 8px;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

.config-content {
  padding: 20px 0;
}

.config-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.config-row label {
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.config-row .el-select {
  margin-bottom: 10px;
}

.dialog-footer {
  text-align: right;
}

.config-display {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: #666;
}

.config-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  padding: 2px 0;
}

.config-item:last-child {
  margin-bottom: 0;
}

.config-label {
  font-weight: 500;
  color: #555;
}

.config-value {
  color: #333;
  font-weight: 600;
}

@media (max-width: 600px) {
  .container {
    padding: 20px;
  }

  .controls {
    flex-direction: column;
    align-items: center;
  }
}
</style>
<style lang="scss">
.config-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 20px 0;

  .config-row {
    display: flex;
    flex-direction: column;

    label {
      font-weight: bold;
      margin-bottom: 8px;
      color: #333;
      font-size: 14px;
    }
  }
}
</style>